"use client";

import React from "react";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useRowActionItems,
  useRowActionItemsWithServices,
  type RowActionItem,
  type RowActionItemsResult,
} from "./use-row-action-items";
import {
  useSimpleTableRowServices,
  type SimpleTableRowServices,
} from "../../contexts/SimpleTableRowServicesContext";
import { groupRowActionItems } from "./row-action-groups";
import { MORE_ACTIONS_LABEL } from "@/lib/actions/action-categories";
import type {
  DetailAction,
  BaseEntity,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { EntityHooks, SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types/shared";

interface RowActionsDropdownMenuProps<T extends BaseEntity> {
  item: T;
  actions: DetailAction<T>[];
  hooks: EntityHooks<T>;
  entityName: string;
  config?: SimpleTableConfig<T>;
}

function renderRow<T extends BaseEntity>({ action, label, isDisabled, isDestructive, run }: RowActionItem<T>) {
  return (
    <DropdownMenuItem
      key={action.id}
      disabled={isDisabled}
      onSelect={() => run()}
      className={isDestructive ? "text-red-600 focus:text-red-600" : ""}
    >
      {label}
    </DropdownMenuItem>
  );
}

/**
 * Render a zone as labeled category sections — matching the right-click menu
 * (RowContextMenuContent). Each category gets a header label EXCEPT destructive,
 * which is pinned last and renders its items with no header.
 */
function renderZone<T extends BaseEntity>(items: RowActionItem<T>[]) {
  return groupRowActionItems(items).map((group, groupIndex) => (
    <React.Fragment key={group.category}>
      {groupIndex > 0 && <DropdownMenuSeparator />}
      {group.category !== "destructive" && (
        <DropdownMenuLabel className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {group.label}
        </DropdownMenuLabel>
      )}
      {group.items.map(renderRow)}
    </React.Fragment>
  ));
}

/**
 * Dropdown version of row actions. Shares the action-building/execution path
 * (`useRowActionItems`) with the mobile bottom-sheet (`MobileRowActionsSheet`)
 * and the desktop right-click context menu. Low-priority actions collapse into
 * a "More…" submenu; destructive actions are pinned last.
 *
 * Split in two so the hoisted table-level services can be used WITHOUT a
 * conditional hook call: `SimpleTableMobileCard` renders one of these per card,
 * and each card was subscribing its own `useUpdate`/`useDelete`/
 * `useActionFiltering` (#2629). Same shape as `RowActions`'s
 * `TableRowActionsBody` / `TableRowActionsWrapper` pair. Outside a table the
 * context is `undefined` and the standalone branch behaves exactly as before.
 */
export function RowActionsDropdownMenu<T extends BaseEntity>(
  props: RowActionsDropdownMenuProps<T>
) {
  const tableServices = useSimpleTableRowServices<T>();
  return tableServices ? (
    <HoistedRowActionsDropdownMenu<T> {...props} services={tableServices} />
  ) : (
    <StandaloneRowActionsDropdownMenu<T> {...props} />
  );
}

function StandaloneRowActionsDropdownMenu<T extends BaseEntity>({
  item,
  actions,
  hooks,
  entityName,
  config,
}: RowActionsDropdownMenuProps<T>) {
  const result = useRowActionItems({ item, actions, hooks, entityName, config });
  return <RowActionsDropdownMenuView {...result} />;
}

function HoistedRowActionsDropdownMenu<T extends BaseEntity>({
  item,
  actions,
  hooks,
  entityName,
  config,
  services,
}: RowActionsDropdownMenuProps<T> & { services: SimpleTableRowServices<T> }) {
  const result = useRowActionItemsWithServices({
    item,
    actions,
    hooks,
    entityName,
    config,
    updateMutation: services.update,
    deleteMutation: services.delete,
    filterActionsByRole: services.filterActionsByRole,
  });
  return <RowActionsDropdownMenuView {...result} />;
}

function RowActionsDropdownMenuView<T extends BaseEntity>({
  ready,
  partition,
}: RowActionItemsResult<T>) {
  if (!ready) {
    return null;
  }

  const { top, more, danger } = partition;

  return (
    <DropdownMenuContent align="end" className="w-56">
      {renderZone(top)}

      {more.length > 0 && (
        <>
          {top.length > 0 && <DropdownMenuSeparator />}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>{MORE_ACTIONS_LABEL}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-56">
              {renderZone(more)}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </>
      )}

      {danger.length > 0 && (
        <>
          {(top.length > 0 || more.length > 0) && <DropdownMenuSeparator />}
          {renderZone(danger)}
        </>
      )}
    </DropdownMenuContent>
  );
}
